export const RECORD_KINDS = [
  'organization',
  'organizational_group',
  'location',
  'department',
  'designation',
  'designation_package',
  'team',
  'employee',
  'equity_grant',
  'management',
  'pay_package',
  'policy_package',
  'payroll_calendar',
  'bonus_policy',
  'leave_policy',
  'overtime_policy',
  'allowance_policy',
  'deduction_policy',
  'commission_policy',
  'attendance_policy',
  'holiday_calendar',
  'tax_profile',
  'tax_code',
  'payment_term',
  'price_list',
  'unit_of_measure',
  'gl_account',
  'cost_increase',
  'expense_policy',
  'supplier',
  'sku',
  'product',
  'product_group',
  'asset_component',
  'asset',
  'asset_group',
  'customer',
  'fleet',
  'project',
  'service',
  'service_category',
  'form',
  'workflow',
] as const;

export type RecordKind = typeof RECORD_KINDS[number];

import type { MasterDataTrackingItem } from './customMasterData';
import {
  CUSTOM_MASTER_PICTURE_URL_VALUE_MAX,
  PICTURE_URL_FIELD_KEY,
  TRACK_PICTURE_URL,
} from './customMasterData';
import { trackerFieldKey } from './trackerFieldKey';

export type RecordFieldType = 'text' | 'textarea' | 'email' | 'tel' | 'number' | 'date' | 'select';

export type RecordFieldDef = {
  key: string
  label: string
  type: RecordFieldType
  required?: boolean
  max: number
  /** Shown under the label — keep optional fields clear about why they exist. */
  hint?: string
  /** Optional tracking item for logs/charts (filled on templates). */
  trackingItem?: MasterDataTrackingItem
  /** Fixed choices for select fields. */
  options?: string[]
  /** Select choices come from records of this kind (searchable name + code). */
  optionsFromKind?: RecordKind
  /** Stored form value: record name (default) or record id for hierarchy links. */
  optionsStoreAs?: 'id' | 'name'
  /** Hidden on admin create — the invited person fills this on the expireable page. */
  filledByInvitee?: boolean
  /** Dropdown of values already used on this kind, plus a custom typed value. */
  optionsFromExistingValues?: boolean
  /** Searchable custom master data types that include a percent field. */
  optionsFromCustomDataPercent?: boolean
  /** Select choices from instances of a custom master data type (by type code). */
  optionsFromCustomTypeCode?: string
  /** New-record form keeps the last typed/saved value for this field. */
  rememberLastInput?: boolean
};

/** Optional public picture URL — same tracker on product, SKU, asset, and asset component. */
export const PICTURE_URL_CATALOG_FIELD: RecordFieldDef = {
  key: PICTURE_URL_FIELD_KEY,
  label: 'Picture URL',
  type: 'text',
  max: CUSTOM_MASTER_PICTURE_URL_VALUE_MAX,
  trackingItem: TRACK_PICTURE_URL,
  hint: 'Optional public image URL for display.',
};

/**
 * Master-data asset condition (Records listing/editor) — same values assets mobility writes
 * on collect/return confirm into fields.condition.
 */
export const ASSET_CONDITION_OPTIONS = ['New', 'Good', 'Fair', 'Poor'] as const;
export type AssetCondition = typeof ASSET_CONDITION_OPTIONS[number];
export const ASSET_CONDITION_FIELD_KEY = 'condition' as const;

/** Operating records scoped under a group/category via parent_id when the field is set. */
export const RECORD_SCOPE_GROUP_FIELD: Partial<Record<
  RecordKind,
  { fieldKey: string, scopeKind: RecordKind }
>> = {
  service: { fieldKey: 'serviceCategory', scopeKind: 'service_category' },
  asset: { fieldKey: 'assetGroup', scopeKind: 'asset_group' },
  product: { fieldKey: 'productGroup', scopeKind: 'product_group' },
};

export const recordScopeGroupField = (
  kind: RecordKind,
): { fieldKey: string, scopeKind: RecordKind } | null => (
  RECORD_SCOPE_GROUP_FIELD[kind] || null
);

export const isSelfParentRecordType = (def: RecordTypeDef): boolean => (
  Boolean(def.parentKind && def.parentKind === def.kind)
);

export type RecordTypeDef = {
  kind: RecordKind
  label: string
  plural: string
  /** Only one row of this kind is allowed. */
  unique?: boolean
  /** Payroll, tax, leave, and similar setup records. */
  isConfig?: boolean
  parentKind?: RecordKind
  parentRequired?: boolean
  childKind?: RecordKind
  childRequiredOnCreate?: boolean
  /** Read-only list derived from other records — no add/edit/delete in UI or API. */
  isAutoFilled?: boolean
  fields: RecordFieldDef[]
};

export const RECORD_CATALOG: RecordTypeDef[] = [
  {
    kind: 'organization',
    label: 'Organization',
    plural: 'Organization',
    unique: true,
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true, max: 80, trackingItem: 'track_head_scope' },
      { key: 'legalName', label: 'Legal name', type: 'text', required: true, max: 120 },
      { key: 'code', label: 'Code', type: 'text', max: 40 },
      { key: 'taxId', label: 'Tax ID', type: 'text', max: 40 },
      { key: 'registrationNumber', label: 'Registration number', type: 'text', max: 40 },
      { key: 'industry', label: 'Industry', type: 'text', max: 80 },
      { key: 'fiscalYearStart', label: 'Fiscal year start', type: 'date', required: true, max: 10 },
      { key: 'phone', label: 'Phone', type: 'tel', max: 40 },
      { key: 'email', label: 'Email', type: 'email', max: 120, trackingItem: 'track_email' },
      { key: 'website', label: 'Website', type: 'text', max: 160 },
      { key: 'address', label: 'Address', type: 'text', required: true, max: 160 },
      { key: 'country', label: 'Country', type: 'select', required: true, max: 80 },
      // Symbol prefix for money figures app-wide (organizations.currency); Settings plans stay USD/₦.
      {
        key: 'currency',
        label: 'Currency',
        type: 'text',
        required: true,
        max: 8,
        hint: 'Symbol shown on money amounts outside Settings plans (e.g. ₦, $, €).',
      },
    ],
  },
  {
    kind: 'organizational_group',
    label: 'Organizational group',
    plural: 'Organizational groups',
    parentKind: 'organization',
    parentRequired: true,
    isAutoFilled: true,
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true, max: 80, trackingItem: 'track_head_scope' },
      { key: 'code', label: 'Code', type: 'text', required: true, max: 40 },
      { key: 'description', label: 'Description', type: 'textarea', max: 400 },
      { key: 'customTypeId', label: 'Custom data id', type: 'text', max: 80 },
    ],
  },
  {
    kind: 'location',
    label: 'Location',
    plural: 'Locations',
    parentKind: 'organization',
    parentRequired: true,
    fields: [
      {
        key: 'name',
        label: 'Name',
        type: 'select',
        required: true,
        max: 80,
        optionsFromExistingValues: true,
        trackingItem: 'track_head_scope',
        hint: 'Pick a name already used, or type a new one.',
      },
      { key: 'code', label: 'Code', type: 'text', required: true, max: 40 },
      { key: 'address', label: 'Address', type: 'text', required: true, max: 160 },
      { key: 'city', label: 'City', type: 'text', required: true, max: 80 },
      { key: 'state', label: 'State / region', type: 'text', max: 80 },
      { key: 'postalCode', label: 'Postal code', type: 'text', max: 24 },
      { key: 'country', label: 'Country', type: 'select', required: true, max: 80 },
      { key: 'phone', label: 'Phone', type: 'tel', max: 40 },
      {
        key: 'floor',
        label: 'Floor',
        type: 'select',
        max: 80,
        optionsFromExistingValues: true,
        hint: 'Pick a floor already used on a location, or type a new one.',
      },
      {
        key: 'status',
        label: 'Status',
        type: 'select',
        required: true,
        max: 24,
        options: ['Active', 'Inactive'],
        hint: 'Active or inactive.',
      },
      {
        key: 'managers',
        label: 'Location manager',
        type: 'select',
        max: 80,
        optionsFromKind: 'employee',
        optionsStoreAs: 'id',
        hint: 'Optional. Employee who manages this location — work orders with a Location object email them.',
      },
    ],
  },
  {
    kind: 'department',
    label: 'Department',
    plural: 'Departments',
    parentKind: 'location',
    parentRequired: true,
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true, max: 80, trackingItem: 'track_head_scope' },
      { key: 'code', label: 'Code', type: 'text', required: true, max: 40 },
      { key: 'costCenter', label: 'Cost center', type: 'text', max: 40 },
      {
        key: 'manager',
        label: 'Department manager',
        type: 'select',
        max: 80,
        optionsFromKind: 'employee',
        optionsStoreAs: 'id',
        hint: 'Optional. Any employee who manages this department (any department, including owners and CEOs).',
      },
      { key: 'email', label: 'Email', type: 'email', max: 120, trackingItem: 'track_email' },
      { key: 'phone', label: 'Phone', type: 'tel', max: 40 },
      {
        key: 'status',
        label: 'Status',
        type: 'select',
        required: true,
        max: 24,
        options: ['Active', 'Inactive'],
        hint: 'Active or inactive.',
      },
    ],
  },
  {
    kind: 'designation',
    label: 'Designation',
    plural: 'Designations',
    parentKind: 'department',
    parentRequired: true,
    fields: [
      {
        key: 'name',
        label: 'Name',
        type: 'text',
        required: true,
        max: 80,
        trackingItem: 'track_head_scope',
        hint: 'Job role under this department (for example Engineering Manager).',
      },
      { key: 'code', label: 'Code', type: 'text', required: true, max: 40 },
      { key: 'grade', label: 'Grade', type: 'select', required: true, max: 40 },
      { key: 'level', label: 'Level', type: 'select', max: 40 },
      {
        key: 'designationPackage',
        label: 'Designation package',
        type: 'select',
        required: true,
        max: 80,
        optionsFromKind: 'designation_package',
        optionsStoreAs: 'id',
        hint: 'Overtime eligibility and leave days for this designation.',
      },
    ],
  },
  {
    kind: 'designation_package',
    label: 'Designation package',
    plural: 'Designation packages',
    isConfig: true,
    parentKind: 'organization',
    parentRequired: true,
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true, max: 80, hint: 'For example Standard leave or Overtime eligible.' },
      { key: 'code', label: 'Code', type: 'text', required: true, max: 40 },
      {
        key: 'overtimeEligible',
        label: 'Overtime eligible',
        type: 'select',
        required: true,
        max: 8,
        options: ['Yes', 'No'],
        trackingItem: 'track_boolean',
        hint: 'Yes or no.',
      },
      {
        key: 'leaveDaysPerYear',
        label: 'Leave days per year',
        type: 'number',
        required: true,
        max: 8,
        trackingItem: 'track_number',
        rememberLastInput: true,
      },
    ],
  },
  {
    kind: 'team',
    label: 'Team',
    plural: 'Teams',
    parentKind: 'department',
    parentRequired: true,
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true, max: 80, trackingItem: 'track_head_scope' },
      { key: 'code', label: 'Code', type: 'text', required: true, max: 40 },
      {
        key: 'supervisor',
        label: 'Supervisor',
        type: 'select',
        required: true,
        max: 80,
        optionsFromKind: 'employee',
        optionsStoreAs: 'id',
        hint: 'Required. Pick an employee in this team\'s department. Teams without a supervisor stay hidden in pickers.',
      },
      {
        key: 'lead',
        label: 'Team lead',
        type: 'select',
        max: 80,
        optionsFromKind: 'employee',
        optionsStoreAs: 'id',
        hint: 'Optional. Lead for this department team — must be in the same department.',
      },
      {
        key: 'purpose',
        label: 'Purpose',
        type: 'text',
        max: 160,
        hint: 'What this team tracks or is for.',
      },
      {
        key: 'status',
        label: 'Status',
        type: 'select',
        required: true,
        max: 24,
        options: ['Active', 'Inactive'],
        hint: 'Active or inactive.',
      },
    ],
  },
  {
    kind: 'employee',
    label: 'Employee',
    plural: 'Employees',
    parentKind: 'designation',
    parentRequired: true,
    fields: [
      {
        key: 'name',
        label: 'Name',
        type: 'text',
        required: true,
        max: 80,
        filledByInvitee: true,
        trackingItem: 'track_head_scope',
      },
      {
        key: 'employeeNumber',
        label: 'Employee number',
        type: 'text',
        required: true,
        max: 40,
        hint: 'Unique staff number. Use Generate to create one.',
      },
      { key: 'hireDate', label: 'Hire date', type: 'date', required: true, max: 10 },
      { key: 'probationEndDate', label: 'Probation end', type: 'date', max: 10 },
      { key: 'contractEndDate', label: 'Contract end', type: 'date', max: 10 },
      {
        key: 'accessType',
        label: 'Access type',
        type: 'select',
        required: true,
        max: 24,
        options: ['Employee', 'Manager', 'CEO'],
        hint: 'Regular employee, manager, or CEO. Only this person\'s manager can set manage access. Organization record stays owner-only. Only one employee can be CEO. CEO can be a manager for one person only.',
      },
      {
        key: 'department',
        label: 'Department',
        type: 'select',
        required: true,
        max: 80,
        optionsFromKind: 'department',
        optionsStoreAs: 'id',
        hint: 'Required before the invite is sent.',
      },
      {
        key: 'manager',
        label: 'Manager',
        type: 'select',
        required: true,
        max: 80,
        optionsFromKind: 'employee',
        optionsStoreAs: 'id',
        hint: 'Required. Manager, CEO (once), or Owner in development — must be in this department.',
      },
      { key: 'dateOfBirth', label: 'Date of birth', type: 'date', max: 10, filledByInvitee: true },
      { key: 'nationalId', label: 'National ID', type: 'text', max: 40, filledByInvitee: true },
      { key: 'taxNumber', label: 'Tax number', type: 'text', max: 40, filledByInvitee: true },
      {
        key: 'email',
        label: 'Employee email',
        type: 'email',
        required: true,
        max: 120,
        hint: 'Required. An invite is sent here so they can fill their own details.',
        trackingItem: 'track_email',
      },
      { key: 'workPhone', label: 'Work phone', type: 'tel', max: 40, filledByInvitee: true },
      {
        key: 'whatsapp',
        label: 'WhatsApp',
        type: 'tel',
        required: true,
        max: 40,
        filledByInvitee: true,
        hint: 'Required. AI uses this number for background work.',
      },
      {
        key: 'status',
        label: 'Status',
        type: 'select',
        required: true,
        max: 24,
        options: ['Active', 'On leave', 'Terminated'],
        hint: 'Active, on leave, or terminated.',
      },
      {
        key: 'compensationType',
        label: 'Compensation',
        type: 'select',
        required: true,
        max: 24,
        options: ['Payroll', 'Equity required'],
        hint: 'Payroll uses a pay package. Equity required uses equity grant records under this person — org-wide or per project.',
      },
      {
        key: 'payPackage',
        label: 'Pay package',
        type: 'select',
        max: 80,
        optionsFromKind: 'pay_package',
        hint: 'Required when compensation is payroll. Leave empty when compensation is equity required.',
      },
      {
        key: 'policyPackage',
        label: 'Policy package',
        type: 'select',
        required: true,
        max: 80,
        optionsFromKind: 'policy_package',
        hint: 'Policy package configuration record. Shared bonus, leave, overtime, and deduction setup.',
      },
      {
        key: 'taxProfile',
        label: 'Tax profile',
        type: 'select',
        max: 80,
        optionsFromKind: 'tax_profile',
        hint: 'Tax profile configuration record.',
      },
      { key: 'bankName', label: 'Bank name', type: 'text', required: true, max: 80, filledByInvitee: true },
      { key: 'bankAccount', label: 'Bank account', type: 'text', required: true, max: 40, filledByInvitee: true },
      { key: 'bankBranch', label: 'Bank branch', type: 'text', max: 80, filledByInvitee: true },
      { key: 'emergencyContact', label: 'Emergency contact', type: 'text', max: 80, filledByInvitee: true },
      { key: 'emergencyPhone', label: 'Emergency phone', type: 'tel', required: true, max: 40, filledByInvitee: true },
    ],
  },
  {
    kind: 'equity_grant',
    label: 'Equity grant',
    plural: 'Equity grants',
    isConfig: true,
    parentKind: 'employee',
    parentRequired: true,
    fields: [
      {
        key: 'name',
        label: 'Name',
        type: 'text',
        required: true,
        max: 80,
        hint: 'For example Seed grant or Atlas stake.',
      },
      { key: 'code', label: 'Code', type: 'text', required: true, max: 40 },
      {
        key: 'compensationScope',
        label: 'Grant scope',
        type: 'select',
        required: true,
        max: 24,
        options: ['Organization', 'Project'],
        hint: 'Organization-wide, or tied to one project.',
      },
      {
        key: 'projectId',
        label: 'Project',
        type: 'select',
        max: 80,
        optionsFromKind: 'project',
        optionsStoreAs: 'id',
        hint: 'Required when grant scope is Project. Success is tracked on that project.',
      },
      {
        key: 'equityPercent',
        label: 'Equity percent',
        type: 'number',
        required: true,
        max: 8,
        trackingItem: 'track_percent',
        hint: 'Share of equity for this grant, from 0 to 100.',
      },
      {
        key: 'status',
        label: 'Status',
        type: 'select',
        required: true,
        max: 24,
        options: ['Active', 'Inactive'],
        hint: 'Active or inactive.',
      },
    ],
  },
  {
    kind: 'management',
    label: 'Manager',
    plural: 'Management',
    isAutoFilled: true,
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true, max: 80, trackingItem: 'track_head_scope' },
      { key: 'employeeNumber', label: 'Employee number', type: 'text', max: 40 },
      { key: 'accessType', label: 'Access type', type: 'select', max: 24 },
      { key: 'department', label: 'Department', type: 'select', max: 80, optionsFromKind: 'department', optionsStoreAs: 'id' },
      { key: 'status', label: 'Status', type: 'select', max: 24 },
      { key: 'email', label: 'Employee email', type: 'email', max: 120, trackingItem: 'track_email' },
    ],
  },
  {
    kind: 'pay_package',
    label: 'Pay package',
    plural: 'Pay packages',
    isConfig: true,
    parentKind: 'organization',
    parentRequired: true,
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true, max: 80, hint: 'For example Full-time, Part-time, Contract, or Expatriate.' },
      { key: 'code', label: 'Code', type: 'text', required: true, max: 40 },
      {
        key: 'employmentType',
        label: 'Employment type',
        type: 'select',
        required: true,
        max: 40,
        hint: 'Full-time, part-time, or contract.',
      },
      {
        key: 'workHoursPerWeek',
        label: 'Work hours per week',
        type: 'number',
        required: true,
        max: 8,
        hint: 'Hours in one week. Highest is 168.',
      },
      { key: 'hourlyRate', label: 'Hourly rate', type: 'number', max: 24 },
      { key: 'baseSalary', label: 'Minimum salary', type: 'number', max: 24 },
      {
        key: 'maxSalary',
        label: 'Maximum salary',
        type: 'number',
        max: 24,
        hint: 'Optional. A top amount, or use percentage maximum instead.',
      },
      {
        key: 'maxSalaryPercent',
        label: 'Percentage maximum',
        type: 'number',
        max: 12,
        hint: 'Optional. A top percent of minimum salary, or use maximum salary instead.',
      },
      {
        key: 'payrollCalendar',
        label: 'Payroll calendar',
        type: 'select',
        required: true,
        max: 80,
        optionsFromKind: 'payroll_calendar',
        hint: 'Payroll calendar configuration record.',
      },
    ],
  },
  {
    kind: 'policy_package',
    label: 'Policy package',
    plural: 'Policy packages',
    isConfig: true,
    parentKind: 'organization',
    parentRequired: true,
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true, max: 80, hint: 'For example Full-time benefits or Contract benefits.' },
      { key: 'code', label: 'Code', type: 'text', required: true, max: 40 },
      { key: 'bonusEligible', label: 'Bonus eligible', type: 'select', max: 8, hint: 'Yes or no.' },
      {
        key: 'bonusPolicy',
        label: 'Bonus policy',
        type: 'select',
        max: 80,
        optionsFromKind: 'bonus_policy',
        hint: 'Bonus policy configuration record.',
      },
      { key: 'commissionEligible', label: 'Commission eligible', type: 'select', max: 8, hint: 'Yes or no.' },
      {
        key: 'commissionPolicy',
        label: 'Commission policy',
        type: 'select',
        max: 80,
        optionsFromKind: 'commission_policy',
        hint: 'Commission policy configuration record.',
      },
      { key: 'overtimeEligible', label: 'Overtime eligible', type: 'select', max: 8, hint: 'Yes or no.' },
      {
        key: 'overtimePolicy',
        label: 'Overtime policy',
        type: 'select',
        max: 80,
        optionsFromKind: 'overtime_policy',
        hint: 'Overtime policy configuration record.',
      },
      {
        key: 'leavePolicy',
        label: 'Leave policy',
        type: 'select',
        max: 80,
        optionsFromKind: 'leave_policy',
        hint: 'Leave policy configuration record.',
      },
      {
        key: 'allowancePolicy',
        label: 'Allowance policy',
        type: 'select',
        max: 80,
        optionsFromKind: 'allowance_policy',
        hint: 'Allowance policy configuration record.',
      },
      {
        key: 'deductionPolicy',
        label: 'Deduction policy',
        type: 'select',
        max: 80,
        optionsFromKind: 'deduction_policy',
        hint: 'Deduction policy configuration record.',
      },
    ],
  },
  {
    kind: 'payroll_calendar',
    label: 'Payroll calendar',
    plural: 'Payroll calendars',
    isConfig: true,
    parentKind: 'organization',
    parentRequired: true,
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true, max: 80 },
      { key: 'code', label: 'Code', type: 'text', required: true, max: 40 },
      { key: 'payFrequency', label: 'Pay frequency', type: 'select', required: true, max: 24, hint: 'Weekly, biweekly, or monthly.' },
      { key: 'periodStartDay', label: 'Period start day', type: 'number', max: 8 },
      { key: 'paydayRule', label: 'Payday rule', type: 'text', max: 80, hint: 'For example last working day or day 25.' },
      { key: 'cutoffDaysBeforePayday', label: 'Cutoff days before payday', type: 'number', max: 8 },
      { key: 'includeOvertime', label: 'Include overtime', type: 'select', max: 8, hint: 'Yes or no.' },
      { key: 'includeBonus', label: 'Include bonus', type: 'select', max: 8, hint: 'Yes or no.' },
    ],
  },
  {
    kind: 'bonus_policy',
    label: 'Bonus policy',
    plural: 'Bonus policies',
    isConfig: true,
    parentKind: 'organization',
    parentRequired: true,
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true, max: 80 },
      { key: 'code', label: 'Code', type: 'text', required: true, max: 40 },
      { key: 'bonusType', label: 'Bonus type', type: 'select', required: true, max: 40, options: ['Annual', 'Performance', 'Signing'], hint: 'Annual, performance, or signing.' },
      { key: 'ratePercent', label: 'Rate percent', type: 'number', max: 12 },
      { key: 'fixedAmount', label: 'Fixed amount', type: 'number', max: 24 },
      { key: 'frequency', label: 'Frequency', type: 'text', max: 24 },
      { key: 'eligibility', label: 'Eligibility', type: 'text', max: 160 },
      { key: 'minMonthsService', label: 'Minimum months of service', type: 'number', max: 8 },
      { key: 'payoutMonth', label: 'Payout month', type: 'text', max: 24 },
      { key: 'taxable', label: 'Taxable', type: 'select', required: true, max: 8, hint: 'Yes or no.' },
    ],
  },
  {
    kind: 'leave_policy',
    label: 'Leave policy',
    plural: 'Leave policies',
    isConfig: true,
    parentKind: 'organization',
    parentRequired: true,
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true, max: 80 },
      { key: 'code', label: 'Code', type: 'text', required: true, max: 40 },
      { key: 'annualDays', label: 'Annual days', type: 'number', required: true, max: 8 },
      { key: 'sickDays', label: 'Sick days', type: 'number', max: 8 },
      { key: 'personalDays', label: 'Personal days', type: 'number', max: 8 },
      { key: 'carryOverDays', label: 'Carry-over days', type: 'number', max: 8 },
      { key: 'carryOverExpiryMonths', label: 'Carry-over expiry months', type: 'number', max: 8 },
      { key: 'unpaidAllowed', label: 'Unpaid leave allowed', type: 'select', max: 8, hint: 'Yes or no.' },
    ],
  },
  {
    kind: 'overtime_policy',
    label: 'Overtime policy',
    plural: 'Overtime policies',
    isConfig: true,
    parentKind: 'organization',
    parentRequired: true,
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true, max: 80 },
      { key: 'code', label: 'Code', type: 'text', required: true, max: 40 },
      { key: 'weekdayMultiplier', label: 'Weekday multiplier', type: 'number', required: true, max: 8 },
      { key: 'weekendMultiplier', label: 'Weekend multiplier', type: 'number', max: 8 },
      { key: 'holidayMultiplier', label: 'Holiday multiplier', type: 'number', max: 8 },
      { key: 'weeklyHoursBeforeOt', label: 'Weekly hours before overtime', type: 'number', required: true, max: 8 },
      { key: 'dailyHoursBeforeOt', label: 'Daily hours before overtime', type: 'number', max: 8 },
    ],
  },
  {
    kind: 'allowance_policy',
    label: 'Allowance policy',
    plural: 'Allowance policies',
    isConfig: true,
    parentKind: 'organization',
    parentRequired: true,
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true, max: 80 },
      { key: 'code', label: 'Code', type: 'text', required: true, max: 40 },
      { key: 'allowanceType', label: 'Allowance type', type: 'select', required: true, max: 40, options: ['Housing', 'Transport', 'Meal'], hint: 'Housing, transport, or meal.' },
      { key: 'amount', label: 'Amount', type: 'number', max: 24 },
      { key: 'percentOfSalary', label: 'Percent of salary', type: 'number', max: 12 },
      { key: 'frequency', label: 'Frequency', type: 'text', max: 24 },
      { key: 'taxable', label: 'Taxable', type: 'select', required: true, max: 8, hint: 'Yes or no.' },
    ],
  },
  {
    kind: 'deduction_policy',
    label: 'Deduction policy',
    plural: 'Deduction policies',
    isConfig: true,
    parentKind: 'organization',
    parentRequired: true,
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true, max: 80 },
      { key: 'code', label: 'Code', type: 'text', required: true, max: 40 },
      { key: 'deductionType', label: 'Deduction type', type: 'select', required: true, max: 40, options: ['Pension', 'Insurance', 'Loan'], hint: 'Pension, insurance, or loan.' },
      { key: 'employeePercent', label: 'Employee percent', type: 'number', max: 12 },
      { key: 'employerPercent', label: 'Employer percent', type: 'number', max: 12 },
      { key: 'fixedAmount', label: 'Fixed amount', type: 'number', max: 24 },
      { key: 'taxable', label: 'Taxable', type: 'select', required: true, max: 8, hint: 'Yes or no.' },
    ],
  },
  {
    kind: 'commission_policy',
    label: 'Commission policy',
    plural: 'Commission policies',
    isConfig: true,
    parentKind: 'organization',
    parentRequired: true,
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true, max: 80 },
      { key: 'code', label: 'Code', type: 'text', required: true, max: 40 },
      { key: 'ratePercent', label: 'Rate percent', type: 'number', required: true, max: 12 },
      { key: 'basis', label: 'Basis', type: 'select', required: true, max: 40, options: ['Revenue', 'Margin'], hint: 'Revenue or margin.' },
      { key: 'threshold', label: 'Threshold', type: 'number', max: 24 },
      { key: 'capAmount', label: 'Cap amount', type: 'number', max: 24 },
      { key: 'frequency', label: 'Frequency', type: 'text', max: 24 },
    ],
  },
  {
    kind: 'attendance_policy',
    label: 'Attendance policy',
    plural: 'Attendance policies',
    isConfig: true,
    parentKind: 'organization',
    parentRequired: true,
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true, max: 80 },
      { key: 'code', label: 'Code', type: 'text', required: true, max: 40 },
      { key: 'workHoursPerDay', label: 'Work hours per day', type: 'number', required: true, max: 8 },
      { key: 'workDaysPerWeek', label: 'Work days per week', type: 'number', required: true, max: 8 },
      { key: 'lateGraceMinutes', label: 'Late grace minutes', type: 'number', max: 8 },
      { key: 'absentDeductsLeave', label: 'Absence deducts leave', type: 'select', max: 8, hint: 'Yes or no.' },
    ],
  },
  {
    kind: 'holiday_calendar',
    label: 'Holiday calendar',
    plural: 'Holiday calendars',
    isConfig: true,
    parentKind: 'organization',
    parentRequired: true,
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true, max: 80 },
      { key: 'code', label: 'Code', type: 'text', required: true, max: 40 },
      { key: 'region', label: 'Region', type: 'text', max: 80 },
      { key: 'year', label: 'Year', type: 'number', required: true, max: 8 },
      { key: 'paidHolidayCount', label: 'Paid holiday count', type: 'number', max: 8 },
    ],
  },
  {
    kind: 'tax_profile',
    label: 'Tax profile',
    plural: 'Tax profiles',
    isConfig: true,
    parentKind: 'organization',
    parentRequired: true,
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true, max: 80 },
      { key: 'code', label: 'Code', type: 'text', required: true, max: 40 },
      { key: 'country', label: 'Country', type: 'select', required: true, max: 80 },
      { key: 'taxIdType', label: 'Tax ID type', type: 'text', max: 40 },
      { key: 'withholdingPercent', label: 'Withholding percent', type: 'number', max: 12 },
      { key: 'filingFrequency', label: 'Filing frequency', type: 'text', max: 24 },
    ],
  },
  {
    kind: 'tax_code',
    label: 'Tax code',
    plural: 'Tax codes',
    isConfig: true,
    parentKind: 'organization',
    parentRequired: true,
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true, max: 80 },
      { key: 'code', label: 'Code', type: 'text', required: true, max: 40 },
      { key: 'ratePercent', label: 'Rate percent', type: 'number', required: true, max: 12 },
      { key: 'taxType', label: 'Tax type', type: 'select', required: true, max: 40, options: ['VAT', 'Sales', 'Withholding'], hint: 'VAT, sales, or withholding.' },
      { key: 'inclusive', label: 'Price inclusive', type: 'select', required: true, max: 8, hint: 'Yes or no.' },
    ],
  },
  {
    kind: 'payment_term',
    label: 'Payment term',
    plural: 'Payment terms',
    isConfig: true,
    parentKind: 'organization',
    parentRequired: true,
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true, max: 80 },
      { key: 'code', label: 'Code', type: 'text', required: true, max: 40 },
      { key: 'dueDays', label: 'Due days', type: 'number', required: true, max: 8 },
      { key: 'discountPercent', label: 'Discount percent', type: 'number', max: 12 },
      { key: 'discountDays', label: 'Discount days', type: 'number', max: 8 },
    ],
  },
  {
    kind: 'price_list',
    label: 'Price list',
    plural: 'Price lists',
    isConfig: true,
    parentKind: 'organization',
    parentRequired: true,
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true, max: 80 },
      { key: 'code', label: 'Code', type: 'text', required: true, max: 40 },
      { key: 'currency', label: 'Currency', type: 'text', required: true, max: 8 },
      { key: 'validFrom', label: 'Valid from', type: 'date', required: true, max: 10 },
      { key: 'validTo', label: 'Valid to', type: 'date', max: 10 },
    ],
  },
  {
    kind: 'unit_of_measure',
    label: 'Unit of measure',
    plural: 'Units of measure',
    isConfig: true,
    parentKind: 'organization',
    parentRequired: true,
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true, max: 80 },
      { key: 'code', label: 'Code', type: 'text', required: true, max: 40 },
      {
        key: 'category',
        label: 'Category',
        type: 'select',
        required: true,
        max: 40,
        options: ['Count', 'Weight', 'Volume', 'Time'],
        hint: 'Count, weight, volume, or time.',
        trackingItem: 'track_measure_category',
      },
      {
        key: 'conversionToBase',
        label: 'Conversion to base',
        type: 'number',
        required: true,
        max: 16,
        trackingItem: 'track_uom',
      },
      {
        key: 'costPrice',
        label: 'Cost price',
        type: 'number',
        max: 24,
        trackingItem: 'track_cost_price',
        hint: 'Optional cost price for this unit.',
      },
      {
        key: 'sellingPrice',
        label: 'Selling price',
        type: 'number',
        required: true,
        max: 24,
        trackingItem: 'track_selling_price',
        hint: 'Required. SKUs and asset components can only select units that have a selling price set. POS sales bills this amount.',
      },
      {
        key: 'percentIncreaseSource',
        label: 'Percent increase source',
        type: 'text',
        max: 80,
        optionsFromCustomDataPercent: true,
        hint: 'Optional custom data type with a percent field. When set, manual percent is disabled.',
      },
      {
        key: 'percentIncreaseManual',
        label: 'Percent increase',
        type: 'number',
        max: 12,
        trackingItem: 'track_percent',
        hint: 'Manual percent increase when no source type is linked.',
      },
    ],
  },
  {
    kind: 'cost_increase',
    label: 'Cost increase',
    plural: 'Cost increases',
    isConfig: true,
    parentKind: 'organization',
    parentRequired: true,
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true, max: 80 },
      { key: 'code', label: 'Code', type: 'text', required: true, max: 40 },
      {
        key: 'increaseMode',
        label: 'Increase mode',
        type: 'select',
        required: true,
        max: 24,
        options: ['Percentage', 'Fixed amount'],
        hint: 'Percentage adds a percent of UOM selling price total; fixed amount adds a flat figure.',
      },
      {
        key: 'increaseValue',
        label: 'Increase value',
        type: 'number',
        required: true,
        max: 16,
        hint: 'Percent (e.g. 15) or fixed amount to add on top of summed UOM selling prices. Defaults to 0 when unset on POS sales.',
      },
    ],
  },
  {
    kind: 'gl_account',
    label: 'GL account',
    plural: 'GL accounts',
    isConfig: true,
    parentKind: 'organization',
    parentRequired: true,
    fields: [
      // track_gl → GL name on activity_logs.action only when a price tracker is present.
      { key: 'name', label: 'Name', type: 'text', required: true, max: 80, trackingItem: 'track_gl' },
      { key: 'code', label: 'Code', type: 'text', required: true, max: 40 },
      { key: 'accountType', label: 'Account type', type: 'select', required: true, max: 40, options: ['Income', 'Expense'], hint: 'Income or expense.' },
      { key: 'parentCode', label: 'Parent account code', type: 'text', max: 40 },
    ],
  },
  {
    kind: 'expense_policy',
    label: 'Expense policy',
    plural: 'Expense policies',
    isConfig: true,
    parentKind: 'organization',
    parentRequired: true,
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true, max: 80 },
      { key: 'code', label: 'Code', type: 'text', required: true, max: 40 },
      { key: 'category', label: 'Category', type: 'text', required: true, max: 80 },
      { key: 'maxAmount', label: 'Maximum amount', type: 'number', required: true, max: 24 },
      { key: 'receiptRequired', label: 'Receipt required', type: 'select', required: true, max: 8, hint: 'Yes or no.' },
      { key: 'approvalRequired', label: 'Approval required', type: 'select', required: true, max: 8, hint: 'Yes or no.' },
    ],
  },
  {
    kind: 'supplier',
    label: 'Supplier',
    plural: 'Suppliers',
    parentKind: 'organization',
    parentRequired: true,
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true, max: 80 },
      { key: 'code', label: 'Code', type: 'text', required: true, max: 40 },
      { key: 'email', label: 'Email', type: 'email', required: true, max: 120, trackingItem: 'track_email' },
      {
        key: 'requestIdentification',
        label: 'Request identification',
        type: 'select',
        max: 8,
        hint: 'Optional. When set to Yes, an identification form link is emailed to this supplier at their email address.',
      },
      { key: 'phone', label: 'Phone', type: 'tel', max: 40, filledByInvitee: true },
      { key: 'taxNumber', label: 'Tax number', type: 'text', max: 40, filledByInvitee: true },
      {
        key: 'paymentTerm',
        label: 'Payment term',
        type: 'select',
        required: true,
        max: 80,
        optionsFromKind: 'payment_term',
        optionsStoreAs: 'id',
        hint: 'Payment term configuration record.',
      },
      { key: 'currency', label: 'Currency', type: 'text', max: 8 },
      {
        key: 'whatsapp',
        label: 'WhatsApp',
        type: 'tel',
        required: true,
        max: 40,
        filledByInvitee: true,
        hint: 'Required. AI uses this number for background work.',
      },
      { key: 'address', label: 'Address', type: 'text', required: true, max: 160, filledByInvitee: true },
      { key: 'contactPerson', label: 'Contact person', type: 'text', max: 80, filledByInvitee: true },
      {
        key: 'status',
        label: 'Status',
        type: 'select',
        required: true,
        max: 24,
        options: ['Active', 'Inactive'],
        hint: 'Active or inactive.',
      },
    ],
  },
  {
    kind: 'sku',
    label: 'SKU',
    plural: 'SKUs',
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true, max: 80 },
      { key: 'code', label: 'Code', type: 'text', required: true, max: 40 },
      { key: 'barcode', label: 'Barcode', type: 'text', max: 64 },
      {
        key: 'unit',
        label: 'Unit of measure',
        type: 'select',
        required: true,
        max: 80,
        optionsFromKind: 'unit_of_measure',
        optionsStoreAs: 'id',
        hint: 'One unit of measure per SKU. Only units with a selling price are listed.',
      },
      {
        key: 'taxCode',
        label: 'Tax code',
        type: 'select',
        max: 40,
        optionsFromKind: 'tax_code',
        optionsStoreAs: 'id',
        hint: 'Tax code configuration record.',
      },
      {
        key: 'glAccount',
        label: 'GL account',
        type: 'select',
        required: true,
        max: 80,
        optionsFromKind: 'gl_account',
        optionsStoreAs: 'id',
        hint: 'GL account configuration record.',
      },
      { key: 'minStock', label: 'Minimum stock', type: 'number', max: 16 },
      { key: 'maxStock', label: 'Maximum stock', type: 'number', max: 16 },
      { key: 'shelfLifeDays', label: 'Shelf life days', type: 'number', max: 8 },
      { key: 'weight', label: 'Weight', type: 'number', max: 16 },
      { ...PICTURE_URL_CATALOG_FIELD },
      {
        key: 'status',
        label: 'Status',
        type: 'select',
        required: true,
        max: 24,
        options: ['Active', 'Inactive'],
        hint: 'Active or inactive.',
      },
    ],
  },
  {
    kind: 'product',
    label: 'Product',
    plural: 'Products',
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true, max: 80 },
      { key: 'code', label: 'Code', type: 'text', required: true, max: 40 },
      {
        key: 'barcode',
        label: 'Barcode',
        type: 'text',
        max: 64,
        hint: 'Optional. Scan or type UPC/EAN — used on POS sales.',
      },
      {
        key: 'productGroup',
        label: 'Product groups',
        type: 'textarea',
        required: true,
        max: 512,
        optionsFromKind: 'product_group',
        optionsStoreAs: 'id',
        hint: 'Required. One or more product groups (name + code). A product may belong to multiple groups.',
      },
      { key: 'brand', label: 'Brand', type: 'text', max: 80 },
      {
        key: 'stock',
        label: 'Stock',
        type: 'number',
        required: true,
        max: 16,
        hint: 'On-hand units at POS. Reduced automatically when a POS sale completes.',
      },
      {
        key: 'skuIds',
        label: 'SKUs',
        type: 'textarea',
        required: true,
        max: 512,
        hint: 'One or more SKUs (name + code). Each SKU needs a UOM with selling price.',
      },
      {
        key: 'taxCode',
        label: 'Tax code',
        type: 'select',
        max: 40,
        optionsFromKind: 'tax_code',
        optionsStoreAs: 'id',
        hint: 'Tax code configuration record.',
      },
      {
        key: 'costIncreaseId',
        label: 'Cost increase',
        type: 'select',
        max: 80,
        optionsFromKind: 'cost_increase',
        optionsStoreAs: 'id',
        hint: 'Optional. Applied on POS sales — percentage or fixed amount on summed UOM selling prices (0 when unset).',
      },
      { key: 'valuationMethod', label: 'Valuation method', type: 'select', required: true, max: 24, options: ['FIFO', 'LIFO', 'Average'], hint: 'FIFO, LIFO, or average.' },
      { key: 'reorderLevel', label: 'Reorder level', type: 'number', max: 16 },
      { ...PICTURE_URL_CATALOG_FIELD },
      {
        key: 'status',
        label: 'Status',
        type: 'select',
        required: true,
        max: 24,
        options: ['Active', 'Inactive'],
        hint: 'Active or inactive.',
      },
    ],
  },
  {
    kind: 'product_group',
    label: 'Product group',
    plural: 'Product groups',
    parentKind: 'product_group',
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true, max: 80 },
      { key: 'code', label: 'Code', type: 'text', required: true, max: 40 },
      {
        key: 'status',
        label: 'Status',
        type: 'select',
        required: true,
        max: 24,
        options: ['Active', 'Inactive'],
        hint: 'Active or inactive.',
      },
    ],
  },
  {
    kind: 'asset_component',
    label: 'Asset component',
    plural: 'Asset components',
    // Optional: assets set parent_id when picking a component; editing the component must keep that link.
    parentKind: 'asset',
    parentRequired: false,
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true, max: 80 },
      { key: 'code', label: 'Code', type: 'text', required: true, max: 40 },
      { key: 'partNumber', label: 'Part number', type: 'text', max: 64 },
      { key: 'manufacturer', label: 'Manufacturer', type: 'text', max: 80 },
      { key: 'quantity', label: 'Quantity', type: 'number', required: true, max: 16 },
      {
        key: 'unit',
        label: 'Unit of measure',
        type: 'select',
        required: true,
        max: 80,
        optionsFromKind: 'unit_of_measure',
        optionsStoreAs: 'id',
        hint: 'UOM with a selling price — used when this component is billed (e.g. POS).',
      },
      { key: 'unitCost', label: 'Unit cost', type: 'number', max: 24 },
      { key: 'warrantyEnd', label: 'Warranty end', type: 'date', max: 10 },
      { key: ASSET_CONDITION_FIELD_KEY, label: 'Condition', type: 'select', max: 40, options: [...ASSET_CONDITION_OPTIONS], hint: 'New, good, fair, or poor.' },
      { ...PICTURE_URL_CATALOG_FIELD },
      {
        key: 'status',
        label: 'Status',
        type: 'select',
        required: true,
        max: 24,
        options: ['Active', 'Replaced', 'Disposed'],
        hint: 'Active, replaced, or disposed.',
      },
    ],
  },
  {
    kind: 'asset',
    label: 'Asset',
    plural: 'Assets',
    parentKind: 'location',
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true, max: 80 },
      { key: 'code', label: 'Code', type: 'text', required: true, max: 40 },
      { key: 'serial', label: 'Serial', type: 'text', max: 80 },
      {
        key: 'barcode',
        label: 'Barcode',
        type: 'text',
        max: 64,
        hint: 'Optional. Scan or type UPC/EAN — used on POS sales.',
      },
      {
        key: 'assetGroup',
        label: 'Asset groups',
        type: 'textarea',
        required: true,
        max: 512,
        optionsFromKind: 'asset_group',
        optionsStoreAs: 'id',
        hint: 'Required. One or more asset groups (name + code). An asset may belong to multiple groups.',
      },
      {
        key: 'assetComponentIds',
        label: 'Asset components',
        type: 'textarea',
        max: 512,
        hint: 'Optional. One or more asset components linked to this asset.',
      },
      { key: 'purchaseDate', label: 'Purchase date', type: 'date', required: true, max: 10 },
      { key: 'purchaseCost', label: 'Purchase cost', type: 'number', required: true, max: 24 },
      { key: 'salvageValue', label: 'Salvage value', type: 'number', max: 24 },
      { key: 'usefulLifeYears', label: 'Useful life years', type: 'number', max: 8 },
      { key: 'depreciationMethod', label: 'Depreciation method', type: 'select', max: 40, options: ['Straight line', 'Declining balance'], hint: 'Straight line or declining balance.' },
      {
        key: 'glAccount',
        label: 'GL account',
        type: 'select',
        required: true,
        max: 80,
        optionsFromKind: 'gl_account',
        optionsStoreAs: 'id',
        hint: 'GL account configuration record.',
      },
      {
        key: 'costIncreaseId',
        label: 'Cost increase',
        type: 'select',
        max: 80,
        optionsFromKind: 'cost_increase',
        optionsStoreAs: 'id',
        hint: 'Optional. Applied on POS sales — percentage or fixed amount on summed UOM selling prices (0 when unset).',
      },
      { key: ASSET_CONDITION_FIELD_KEY, label: 'Condition', type: 'select', required: true, max: 40, options: [...ASSET_CONDITION_OPTIONS], hint: 'New, good, fair, or poor. Updated on assets mobility collect/return.' },
      { key: 'warrantyEnd', label: 'Warranty end', type: 'date', max: 10 },
      { key: 'assignedTo', label: 'Assigned to', type: 'text', max: 80 },
      { ...PICTURE_URL_CATALOG_FIELD },
      {
        key: 'status',
        label: 'Status',
        type: 'select',
        required: true,
        max: 24,
        options: ['Active', 'On hold', 'Reserved', 'Checked in', 'In use', 'Idle', 'Disposed'],
        hint: 'Active, on hold, reserved, checked in, in use, idle, or disposed.',
      },
    ],
  },
  {
    kind: 'asset_group',
    label: 'Asset group',
    plural: 'Asset groups',
    parentKind: 'asset_group',
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true, max: 80 },
      { key: 'code', label: 'Code', type: 'text', required: true, max: 40 },
      {
        key: 'status',
        label: 'Status',
        type: 'select',
        required: true,
        max: 24,
        options: ['Active', 'Inactive'],
        hint: 'Active or inactive.',
      },
    ],
  },
  {
    kind: 'customer',
    label: 'Customer',
    plural: 'Customers',
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true, max: 80 },
      { key: 'code', label: 'Code', type: 'text', required: true, max: 40 },
      { key: 'email', label: 'Email', type: 'email', max: 120, trackingItem: 'track_email' },
      { key: 'phone', label: 'Phone', type: 'tel', max: 40 },
      { key: 'whatsapp', label: 'WhatsApp', type: 'tel', max: 40 },
      { key: 'telegram', label: 'Telegram', type: 'text', max: 40 },
      {
        key: trackerFieldKey('track_customer_id_doc', 'text'),
        label: 'ID document',
        type: 'text',
        max: 400,
        trackingItem: 'track_customer_id_doc',
        hint: 'GCS path to uploaded guest ID document.',
      },
      {
        key: trackerFieldKey('track_customer_face_photo', 'text'),
        label: 'Face photo',
        type: 'text',
        max: 400,
        trackingItem: 'track_customer_face_photo',
        hint: 'GCS path to cropped guest face photo.',
      },
      {
        key: trackerFieldKey('track_customer_booking_count', 'number'),
        label: 'Booking count',
        type: 'number',
        max: 16,
        trackingItem: 'track_customer_booking_count',
        hint: 'Current loyalty discount cycle booking count.',
      },
      {
        key: trackerFieldKey('track_customer_lifetime_bookings', 'number'),
        label: 'Lifetime bookings',
        type: 'number',
        max: 16,
        trackingItem: 'track_customer_lifetime_bookings',
      },
      { key: 'taxNumber', label: 'Tax number', type: 'text', max: 40 },
      { key: 'billingAddress', label: 'Billing address', type: 'text', max: 160 },
      { key: 'shippingAddress', label: 'Shipping address', type: 'text', max: 160 },
      {
        key: 'paymentTerm',
        label: 'Payment term',
        type: 'select',
        max: 80,
        optionsFromKind: 'payment_term',
        optionsStoreAs: 'id',
        hint: 'Payment term configuration record.',
      },
      { key: 'creditLimit', label: 'Credit limit', type: 'number', max: 24 },
      {
        key: 'balanceDue',
        label: 'Balance due',
        type: 'number',
        max: 24,
        hint: 'Outstanding amount this customer owes. Defaults to 0 until charged.',
      },
      { key: 'currency', label: 'Currency', type: 'text', max: 8 },
      {
        key: 'priceList',
        label: 'Price list',
        type: 'select',
        max: 80,
        optionsFromKind: 'price_list',
        optionsStoreAs: 'id',
        hint: 'Price list configuration record.',
      },
      { key: 'contactPerson', label: 'Contact person', type: 'text', max: 80 },
      {
        key: 'status',
        label: 'Status',
        type: 'select',
        required: true,
        max: 24,
        options: ['Active', 'Inactive'],
        hint: 'Active or inactive.',
      },
    ],
  },
  {
    kind: 'fleet',
    label: 'Fleet',
    plural: 'Fleet',
    parentKind: 'location',
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true, max: 80 },
      { key: 'plate', label: 'Plate', type: 'text', required: true, max: 40 },
      { key: 'vin', label: 'VIN', type: 'text', max: 40 },
      { key: 'vehicleType', label: 'Vehicle type', type: 'text', required: true, max: 40 },
      { key: 'make', label: 'Make', type: 'text', required: true, max: 40 },
      { key: 'model', label: 'Model', type: 'text', required: true, max: 40 },
      { key: 'year', label: 'Year', type: 'number', max: 8 },
      { key: 'capacity', label: 'Capacity', type: 'text', max: 24 },
      { key: 'fuelType', label: 'Fuel type', type: 'text', max: 24 },
      { key: 'odometer', label: 'Odometer', type: 'number', max: 16 },
      { key: 'insuranceExpiry', label: 'Insurance expiry', type: 'date', max: 10 },
      { key: 'registrationExpiry', label: 'Registration expiry', type: 'date', max: 10 },
      { key: 'assignedTo', label: 'Assigned to', type: 'text', max: 80 },
      {
        key: 'status',
        label: 'Status',
        type: 'select',
        required: true,
        max: 24,
        options: ['Active', 'In service', 'Retired'],
        hint: 'Active, in service, or retired.',
      },
    ],
  },
  {
    kind: 'project',
    label: 'Project',
    plural: 'Projects',
    parentKind: 'organization',
    parentRequired: true,
    fields: [
      {
        key: 'name',
        label: 'Name',
        type: 'text',
        required: true,
        max: 80,
        trackingItem: 'track_head_scope',
        hint: 'Logged when you update project success.',
      },
      { key: 'code', label: 'Code', type: 'text', required: true, max: 40 },
      {
        key: 'successPercent',
        label: 'Success percent',
        type: 'number',
        required: true,
        max: 8,
        trackingItem: 'track_percent',
        hint: 'How far this project has succeeded, from 0 to 100. This is the project tracker for now.',
      },
      {
        key: 'status',
        label: 'Status',
        type: 'select',
        required: true,
        max: 24,
        options: ['Active', 'On hold', 'Completed', 'Cancelled'],
        hint: 'Active, on hold, completed, or cancelled.',
      },
      {
        key: 'description',
        label: 'Description',
        type: 'textarea',
        max: 400,
        hint: 'Optional. What this project is for.',
      },
    ],
  },
  {
    kind: 'service',
    label: 'Service',
    plural: 'Services',
    parentKind: 'department',
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true, max: 80 },
      { key: 'code', label: 'Code', type: 'text', required: true, max: 40 },
      {
        key: 'serviceCategory',
        label: 'Service categories',
        type: 'textarea',
        required: true,
        max: 512,
        optionsFromKind: 'service_category',
        optionsStoreAs: 'id',
        hint: 'Required. One or more service categories (name + code). A service may belong to multiple categories.',
      },
      { key: 'durationMinutes', label: 'Duration minutes', type: 'number', required: true, max: 8 },
      { key: 'price', label: 'Price', type: 'number', required: true, max: 24 },
      { key: 'cost', label: 'Cost', type: 'number', max: 24 },
      {
        key: 'taxCode',
        label: 'Tax code',
        type: 'select',
        max: 40,
        optionsFromKind: 'tax_code',
        optionsStoreAs: 'id',
        hint: 'Tax code configuration record.',
      },
      { key: 'billingUnit', label: 'Billing unit', type: 'select', required: true, max: 24, options: ['Month', 'Year', 'Hour', 'Day', 'Project', 'Session', 'Usage/unit'], hint: 'Month, year, hour, day, project, session, or usage/unit.' },
      {
        key: 'glAccount',
        label: 'GL account',
        type: 'select',
        required: true,
        max: 80,
        optionsFromKind: 'gl_account',
        optionsStoreAs: 'id',
        hint: 'GL account configuration record.',
      },
      {
        key: 'status',
        label: 'Status',
        type: 'select',
        required: true,
        max: 24,
        options: ['Active', 'Inactive'],
        hint: 'Active or inactive.',
      },
    ],
  },
  {
    kind: 'service_category',
    label: 'Service category',
    plural: 'Service categories',
    parentKind: 'service_category',
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true, max: 80 },
      { key: 'code', label: 'Code', type: 'text', required: true, max: 40 },
      {
        key: 'status',
        label: 'Status',
        type: 'select',
        required: true,
        max: 24,
        options: ['Active', 'Inactive'],
        hint: 'Active or inactive.',
      },
    ],
  },
  /**
   * OPERATIONS TAB (future): Work orders are filled from automatically sent messages
   * so replies can land in activity logs. HR & People drives employee signup
   * (/form/:token) — do not go back to a hardcoded /employee-invite field list.
   */
  {
    kind: 'form',
    label: 'Work order',
    plural: 'Work orders',
    parentKind: 'department',
    parentRequired: true,
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true, max: 80 },
      {
        key: 'code',
        label: 'Code',
        type: 'text',
        required: true,
        max: 40,
        hint: 'Company code for this work order. Use Generate for the next WO-###.',
      },
      {
        key: 'description',
        label: 'Description',
        type: 'textarea',
        max: 20000,
        hint: 'Optional. Staff notes on Basic. On Document work orders this is the page the recipient reads.',
      },
      {
        key: 'status',
        label: 'Status',
        type: 'select',
        required: true,
        max: 24,
        options: ['Active', 'Inactive'],
        hint: 'Active or inactive.',
      },
      {
        key: 'workOrderKind',
        label: 'Work order type',
        type: 'select',
        required: true,
        max: 24,
        options: [
          'Basic',
          'Assets mobility',
          'Document',
          'Directive',
          'Sale',
          'Expense',
          'Direct entry',
          'POS sales',
        ],
        hint: 'Basic for standard ops. Assets mobility assign/return assets. Sale/expense link priced data. POS sales opens a register. Direct entry is manual description + price (no linked data). Document is a full-screen acknowledge page.',
      },
      {
        key: 'workOrderAssetLinkKind',
        label: 'Asset link custom data type',
        type: 'text',
        max: 80,
        trackingItem: 'track_head_scope',
        hint: 'Custom type code of the linked item. Required when Work order type is Sale, Expense, or POS sales.',
      },
      {
        key: 'workOrderAssetLinkId',
        label: 'Asset link custom data record',
        type: 'text',
        max: 80,
        trackingItem: 'track_head_scope',
        hint: 'Custom data record id. Price from UOM (selling for Sale, cost for Expense). Empty when Basic.',
      },
      {
        key: 'workOrderDirectEntryPrice',
        label: 'Direct entry price',
        type: 'text',
        max: 24,
        hint: 'Shown when direction is sale/expense and price mode is General — non-negative amount.',
      },
      {
        key: 'workOrderDirectEntryDirection',
        label: 'Direct entry direction',
        type: 'select',
        max: 16,
        options: ['None', 'Sale', 'Expense'],
        hint: 'None skips money UI; sale/expense shows price mode below.',
      },
      {
        key: 'workOrderDirectEntryPriceMode',
        label: 'Direct entry price mode',
        type: 'select',
        max: 16,
        options: ['Data', 'General'],
        hint: 'Shown when direction is sale/expense. Data = catalog rows + per-item prices; General = single price.',
      },
      {
        key: 'workOrderDirectEntryLinkIds',
        label: 'Direct entry linked records',
        type: 'text',
        max: 4000,
        hint: 'Shown when price mode is Data — multiselect catalog record ids.',
      },
      {
        key: 'workOrderDirectEntryItemPrices',
        label: 'Direct entry item prices',
        type: 'textarea',
        max: 20000,
        hint: 'Shown when price mode is Data — JSON map of record id → price.',
      },
      {
        key: 'workOrderObjects',
        label: 'Required work order object',
        type: 'textarea',
        required: false,
        max: 4000,
        hint: 'Legacy — required objects are set on each operation. Kept for older rows until copied onto ops.',
      },
      {
        key: 'workOrderStructuralHeadScope',
        label: 'Structural Groups head scope',
        type: 'text',
        max: 80,
        hint: 'Legacy field — no longer used.',
      },
      {
        key: 'workOrderObjectIncludeOrder',
        label: 'Include object order',
        type: 'select',
        max: 8,
        options: ['Yes', 'No'],
        hint: 'When Yes, linked operations must fill a required multiline object order field.',
      },
      {
        key: 'formFields',
        label: 'Work order fields',
        type: 'textarea',
        max: 200000,
        hint: 'Dynamic fields the recipient fills. Built in the work order editor.',
      },
    ],
  },
  {
    kind: 'workflow',
    label: 'Workflow',
    plural: 'Workflows',
    parentKind: 'organization',
    parentRequired: true,
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true, max: 80 },
      {
        key: 'description',
        label: 'Description',
        type: 'textarea',
        max: 400,
        hint: 'Optional. Shown when viewing this workflow.',
      },
      {
        key: 'status',
        label: 'Status',
        type: 'select',
        required: true,
        max: 24,
        options: ['Draft', 'Published'],
        hint: 'Draft or published.',
      },
      {
        key: 'workflowGraph',
        label: 'Workflow graph',
        type: 'textarea',
        max: 500000,
        hint: 'Built in the workflow builder.',
      },
    ],
  },
];

export const DEFAULT_RECORD_KIND: RecordKind = 'organization';

export const recordTypeByKind = (kind: unknown): RecordTypeDef | null => {
  const k = String(kind || '').trim().toLowerCase();
  return RECORD_CATALOG.find((t) => t.kind === k) || null;
};

export const isAutoFilledRecordKind = (kind: RecordKind | string): boolean => (
  Boolean(recordTypeByKind(kind)?.isAutoFilled)
);

export const RECORD_NAV_GROUP_LABELS = [
  'Structure master data',
  'Configuration master data',
  'Operating master data',
] as const;

export type RecordNavGroupLabel = typeof RECORD_NAV_GROUP_LABELS[number];

/** Templates overlay — Structure is only head scopes in the default sidebar. */
export const RECORD_TEMPLATE_NAV_GROUP_LABELS = [
  'Configuration master data',
  'Operating master data',
] as const;

const STRUCTURE_RECORD_KINDS: RecordKind[] = [
  'organization',
  'organizational_group',
  'location',
  'department',
  'designation',
  'employee',
  'management',
  'team',
];

/** Left-pane section title for a record type. */
export const recordNavGroupLabel = (type: RecordTypeDef): RecordNavGroupLabel => {
  if (STRUCTURE_RECORD_KINDS.includes(type.kind)) return 'Structure master data';
  if (type.isConfig) return 'Configuration master data';
  return 'Operating master data';
};

export type RecordNavSubgroupLabel =
  | 'Human Resources'
  | 'Products & Sales'
  | 'Custom'
  | 'Customers'
  | 'Value Objects (VO)'
  | 'Business Objects (BO)';

export const CONFIG_HR_KINDS: RecordKind[] = [
  'designation_package',
  'pay_package',
  'policy_package',
  'payroll_calendar',
  'bonus_policy',
  'leave_policy',
  'overtime_policy',
  'allowance_policy',
  'deduction_policy',
  'commission_policy',
  'attendance_policy',
  'holiday_calendar',
  'tax_profile',
  'tax_code',
  'equity_grant',
];

const CONFIG_PRODUCTS_SALES_KINDS: RecordKind[] = [
  'payment_term',
  'price_list',
  'unit_of_measure',
  'gl_account',
  'cost_increase',
  'expense_policy',
];

const OPERATING_CUSTOMERS_KINDS: RecordKind[] = [
  'customer',
];

const OPERATING_VO_KINDS: RecordKind[] = [
  'product_group',
  'asset_group',
  'service_category',
  'supplier',
  'sku',
  'product',
  'asset_component',
  'asset',
  'fleet',
  'project',
  'service',
];

const OPERATING_BO_KINDS: RecordKind[] = [
  'form',
  // 'workflow',
];

/** Nested header inside Configuration / Operating. Structure stays a flat list. */
export const recordNavSubgroupLabel = (type: RecordTypeDef): RecordNavSubgroupLabel | null => {
  if (STRUCTURE_RECORD_KINDS.includes(type.kind)) return null;
  if (CONFIG_HR_KINDS.includes(type.kind)) return 'Human Resources';
  if (CONFIG_PRODUCTS_SALES_KINDS.includes(type.kind)) return 'Products & Sales';
  if (OPERATING_CUSTOMERS_KINDS.includes(type.kind)) return 'Customers';
  if (OPERATING_VO_KINDS.includes(type.kind)) return 'Value Objects (VO)';
  if (OPERATING_BO_KINDS.includes(type.kind)) return 'Business Objects (BO)';
  return null;
};

export const recordNavSubgroupsForGroup = (
  group: RecordNavGroupLabel,
): RecordNavSubgroupLabel[] => {
  if (group === 'Configuration master data') {
    return ['Human Resources', 'Products & Sales', 'Custom'];
  }
  if (group === 'Operating master data') {
    return ['Customers', 'Business Objects (BO)', 'Value Objects (VO)', 'Custom'];
  }
  return [];
};

export type RecordFoundationFlags = {
  organization: boolean
  location: boolean
  department: boolean
  designation_package: boolean
  designation: boolean
  team: boolean
  pay_package: boolean
  policy_package: boolean
  employee: boolean
  unit_of_measure: boolean
  sku: boolean
  asset_component: boolean
  product_group: boolean
  asset_group: boolean
  service_category: boolean
};

export const RECORD_FOUNDATION_KINDS: Array<keyof RecordFoundationFlags> = [
  'organization',
  'location',
  'department',
  'designation_package',
  'designation',
  'team',
  'pay_package',
  'policy_package',
  'employee',
  'unit_of_measure',
  'sku',
  'asset_component',
  'product_group',
  'asset_group',
  'service_category',
];

export const recordFoundationFlagsFromKindSet = (
  present: Set<string>,
): RecordFoundationFlags => ({
  organization: present.has('organization'),
  location: present.has('location'),
  department: present.has('department'),
  designation_package: present.has('designation_package'),
  designation: present.has('designation'),
  team: present.has('team'),
  pay_package: present.has('pay_package'),
  policy_package: present.has('policy_package'),
  employee: present.has('employee'),
  unit_of_measure: present.has('unit_of_measure'),
  sku: present.has('sku'),
  asset_component: present.has('asset_component'),
  product_group: present.has('product_group'),
  asset_group: present.has('asset_group'),
  service_category: present.has('service_category'),
});

/** True when a UOM has any selling price value set (including 0). */
export const uomFieldsHaveSellingPrice = (
  fields?: Record<string, string> | null,
): boolean => String(fields?.sellingPrice ?? '').trim() !== '';

export const uomRecordHasSellingPrice = (row: {
  kind?: string,
  fields?: Record<string, string> | null,
}): boolean => (
  row.kind === 'unit_of_measure' && uomFieldsHaveSellingPrice(row.fields)
);

/** True when a UOM has any cost price value set (including 0). */
export const uomFieldsHaveCostPrice = (
  fields?: Record<string, string> | null,
): boolean => String(fields?.costPrice ?? '').trim() !== '';

export const uomRecordHasCostPrice = (row: {
  kind?: string,
  fields?: Record<string, string> | null,
}): boolean => (
  row.kind === 'unit_of_measure' && uomFieldsHaveCostPrice(row.fields)
);

export const recordFoundationFlags = (
  records: Array<{ kind: string, fields?: Record<string, string> | null }>,
): RecordFoundationFlags => {
  const flags = recordFoundationFlagsFromKindSet(new Set(records.map((r) => r.kind)));
  // SKU unlock needs at least one UOM that already has a selling price.
  flags.unit_of_measure = records.some((r) => uomRecordHasSellingPrice(r));
  return flags;
};

/** Organization → Locations → Departments, then operating records. Employee still needs designation + packages. */
const lockedUntilDepartment = (has: RecordFoundationFlags): string | null => {
  if (!has.organization) return 'Add an Organization record first';
  if (!has.location) return 'Add a Location record first';
  if (!has.department) return 'Add a Department record first';
  return null;
};

export const recordKindLockedReason = (
  kind: RecordKind,
  has: RecordFoundationFlags,
): string | null => {
  if (kind === 'organization') return null;
  if (kind === 'location') {
    return has.organization ? null : 'Add an Organization record first';
  }
  if (kind === 'department') {
    if (!has.organization) return 'Add an Organization record first';
    if (!has.location) return 'Add a Location record first';
    return null;
  }
  if (kind === 'designation') {
    const untilDept = lockedUntilDepartment(has);
    if (untilDept) return untilDept;
    if (!has.designation_package) return 'Add a Designation package record first';
    return null;
  }
  if (kind === 'team') {
    return lockedUntilDepartment(has);
  }
  const typeDef = recordTypeByKind(kind);
  if (typeDef?.isConfig && typeDef.parentKind === 'organization') {
    return lockedUntilDepartment(has);
  }
  if (kind === 'employee') {
    const untilDept = lockedUntilDepartment(has);
    if (untilDept) return untilDept;
    if (!has.designation) return 'Add a Designation record first';
    if (!has.pay_package) return 'Add a Pay package record first';
    if (!has.policy_package) return 'Add a Policy package record first';
    return null;
  }
  if (kind === 'management') {
    const untilDept = lockedUntilDepartment(has);
    if (untilDept) return untilDept;
    if (!has.designation) return 'Add a Designation record first';
    if (!has.employee) return 'Add an Employee record first';
    return null;
  }
  if (kind === 'equity_grant') {
    const untilDept = lockedUntilDepartment(has);
    if (untilDept) return untilDept;
    if (!has.employee) return 'Add an Employee record first';
    return null;
  }

  if (kind === 'sku') {
    const untilDept = lockedUntilDepartment(has);
    if (untilDept) return untilDept;
    if (!has.unit_of_measure) return 'Add a Unit of measure with a selling price first';
    return null;
  }

  if (kind === 'product') {
    const untilDept = lockedUntilDepartment(has);
    if (untilDept) return untilDept;
    if (!has.sku) return 'Add a SKU record first';
    if (!has.product_group) return 'Add a Product group record first';
    return null;
  }

  if (kind === 'asset') {
    const untilDept = lockedUntilDepartment(has);
    if (untilDept) return untilDept;
    if (!has.asset_group) return 'Add an Asset group record first';
    return null;
  }

  if (kind === 'service') {
    const untilDept = lockedUntilDepartment(has);
    if (untilDept) return untilDept;
    if (!has.service_category) return 'Add a Service category record first';
    return null;
  }

  return lockedUntilDepartment(has);
};

export const emptyRecordValues = (def: RecordTypeDef): Record<string, string> => {
  const next: Record<string, string> = {};
  def.fields.forEach((field) => {
    next[field.key] = '';
  });
  if (def.kind === 'employee') next.compensationType = 'Payroll';
  if (def.kind === 'equity_grant') next.compensationScope = 'Organization';
  if (def.kind === 'project') next.successPercent = '0';
  return next;
};

/** Same name, floor, and address details — used to block duplicate locations. */
export const locationRecordMatchKey = (
  name: string,
  fields: Record<string, string>,
): string => (
  [
    name,
    fields.floor,
    fields.address,
    fields.city,
    fields.state,
    fields.postalCode,
    fields.country,
  ].map((part) => String(part || '').trim().toLowerCase()).join('|')
);

/** 24 hours × 7 days — a week cannot hold more hours than this. */
export const HOURS_IN_ONE_WEEK = 168;

/** Standard payroll year — hourly × hours/week × 52 = annual base salary. */
export const WEEKS_IN_ONE_YEAR = 52;

const parsePositiveNumber = (raw: string): number | null => {
  const value = Number(String(raw || '').replace(/,/g, '').trim());
  if (!Number.isFinite(value) || value <= 0) return null;
  return value;
};

/** Hours in one year from the package week length. */
export const payPackageAnnualHours = (workHoursPerWeek: string): number | null => {
  const hours = parsePositiveNumber(workHoursPerWeek);
  if (hours == null || hours > HOURS_IN_ONE_WEEK) return null;
  return hours * WEEKS_IN_ONE_YEAR;
};

export const payPackageBaseSalaryFromHourly = (
  hourlyRate: string,
  workHoursPerWeek: string,
): number | null => {
  const hourly = parsePositiveNumber(hourlyRate);
  const annualHours = payPackageAnnualHours(workHoursPerWeek);
  if (hourly == null || annualHours == null) return null;
  return hourly * annualHours;
};

export const payPackageHourlyFromBaseSalary = (
  baseSalary: string,
  workHoursPerWeek: string,
): number | null => {
  const base = parsePositiveNumber(baseSalary);
  const annualHours = payPackageAnnualHours(workHoursPerWeek);
  if (base == null || annualHours == null) return null;
  return base / annualHours;
};

const formatPayPackageMoney = (amount: number, currency: string): string => (
  `${currency}${amount.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
);

/** While typing hourly or base, show what the other amount would be. */
export const formatPayPackageCompensationCounterpart = (
  key: 'hourlyRate' | 'baseSalary',
  fields: Record<string, string>,
  currency: string,
): string => {
  const hours = fields.workHoursPerWeek || '';
  if (key === 'hourlyRate') {
    const amount = payPackageBaseSalaryFromHourly(fields.hourlyRate || '', hours);
    if (amount == null) return '';
    return `Minimum salary ${formatPayPackageMoney(amount, currency)}`;
  }
  const amount = payPackageHourlyFromBaseSalary(fields.baseSalary || '', hours);
  if (amount == null) return '';
  return `Hourly rate ${formatPayPackageMoney(amount, currency)}`;
};

export const workHoursPerWeekError = (raw: string): string | null => {
  const value = String(raw || '').trim();
  if (!value) return null;
  const hours = Number(value);
  if (!Number.isFinite(hours) || hours < 0) {
    return 'Work hours per week must be a number';
  }
  if (hours > HOURS_IN_ONE_WEEK) {
    return 'Work hours per week cannot be more than 168 (one week)';
  }
  return null;
};

/** Validate a percent field is a number from 0 to 100. */
export const percentRangeError = (raw: string): string | null => {
  const value = String(raw || '').trim();
  if (!value) return null;
  const percent = Number(value);
  if (!Number.isFinite(percent)) return 'Target percent must be a number';
  if (percent < 0 || percent > 100) return 'Target percent must be from 0 to 100';
  return null;
};

export const EMPLOYEE_COMPENSATION_TYPES = ['Payroll', 'Equity required'] as const;
export type EmployeeCompensationType = typeof EMPLOYEE_COMPENSATION_TYPES[number];

export const normalizeEmployeeCompensationType = (raw: unknown): EmployeeCompensationType => (
  String(raw || '').trim() === 'Equity required' ? 'Equity required' : 'Payroll'
);

export const employeePayPackageRequired = (compensationType: unknown): boolean => (
  normalizeEmployeeCompensationType(compensationType) === 'Payroll'
);

/** Pay package is required only for payroll compensation. */
export const employeeCompensationFieldsError = (
  fields: Record<string, string>,
): string | null => {
  if (employeePayPackageRequired(fields.compensationType)
    && !String(fields.payPackage || '').trim()) {
    return 'Pay package is required when compensation is payroll';
  }
  return null;
};

export const EQUITY_GRANT_SCOPES = ['Organization', 'Project'] as const;
export type EquityGrantScope = typeof EQUITY_GRANT_SCOPES[number];

export const normalizeEquityGrantScope = (raw: unknown): EquityGrantScope => (
  String(raw || '').trim() === 'Project' ? 'Project' : 'Organization'
);

/** Equity grant must have a percent; project pick is required for per-project grants. */
export const equityGrantFieldsError = (
  fields: Record<string, string>,
): string | null => {
  const percentRaw = String(fields.equityPercent || '').trim();
  if (!percentRaw) return 'Equity percent is required';
  const percentError = percentRangeError(percentRaw);
  if (percentError) return percentError.replace('Target percent', 'Equity percent');
  if (normalizeEquityGrantScope(fields.compensationScope) === 'Project'
    && !String(fields.projectId || '').trim()) {
    return 'Pick a project when the grant is per project';
  }
  return null;
};

export const projectSuccessPercentError = (raw: string): string | null => {
  const value = String(raw || '').trim();
  if (!value) return 'Success percent is required';
  const percentError = percentRangeError(value);
  return percentError ? percentError.replace('Target percent', 'Success percent') : null;
};

/** Pay package must use base salary or hourly rate — at least one. */
export const payPackageCompensationError = (
  baseSalary: string,
  hourlyRate: string,
): string | null => {
  const hasBase = Boolean(String(baseSalary || '').trim());
  const hasHourly = Boolean(String(hourlyRate || '').trim());
  if (!hasBase && !hasHourly) {
    return 'Minimum salary or Hourly rate is required';
  }
  return null;
};

/** Percentage maximum is a percent of minimum salary. */
export const salaryPercentMaximumAmount = (
  minSalary: string,
  percent: string,
): number | null => {
  const min = Number(String(minSalary || '').replace(/,/g, '').trim());
  const pct = Number(String(percent || '').replace(/,/g, '').trim());
  if (!Number.isFinite(min) || !Number.isFinite(pct) || min <= 0 || pct <= 0) return null;
  return min * (pct / 100);
};

export const formatSalaryPercentMaximumAmount = (
  minSalary: string,
  percent: string,
  currency: string,
): string => {
  const amount = salaryPercentMaximumAmount(minSalary, percent);
  if (amount == null) return '';
  return `${currency}${amount.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

export const EMPLOYEE_REMOVAL_REASONS = [
  'Terminated',
  'Resigned',
  'Suspended',
  'Laid off',
  'Contract ended',
  'Retired',
  'Transfer',
  'Deceased',
  'Invite cancelled',
  'Other',
] as const;

export const EMPLOYEE_REMOVAL_REASON_OTHER = 'Other';

/** Removing an employee always needs an ERP reason. Other needs a short note. */
export const employeeRemovalReasonError = (
  reason: string,
  note: string,
): string | null => {
  const picked = String(reason || '').trim();
  if (!picked) return 'A reason is required to remove this employee';
  if (!(EMPLOYEE_REMOVAL_REASONS as readonly string[]).includes(picked)) {
    return 'Pick a valid removal reason';
  }
  if (picked === EMPLOYEE_REMOVAL_REASON_OTHER && !String(note || '').trim()) {
    return 'Say why you chose Other';
  }
  return null;
};
